import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { styles } from '../../../styles/themes/style';
import { useAuthStore } from '../../../stores/auth/useAuthStore';
import { AppRoutes } from '../../../interfaces/store/appStore';
import { useExtensionRequest } from '../../../services/apis/extension/useExtensionRequest';
import { useCampaignsRequest } from '../../../services/apis/campaigns/useCampaignsRequest';
import { SequenceStepType } from '../../../interfaces/models/campaign';
import Column from '../../../components/Column';
import Row from '../../../components/Row';
import Text from '../../../components/Text';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toFsdProfileUrn(linkedinId: string): string {
  const trimmed = linkedinId.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('urn:li:fsd_profile:')) return trimmed;
  if (trimmed.startsWith('urn:li:member:')) return trimmed.replace('urn:li:member:', 'urn:li:fsd_profile:');
  return `urn:li:fsd_profile:${trimmed}`;
}

function extractLatestPostForTest(lnPosts: any): { postId: string; ugcPostId: string } | null {
  if (!lnPosts?.included) return null;
  const update = lnPosts.included.find(
    (item: any) => item.$type === 'com.linkedin.voyager.dash.feed.Update'
  );
  if (!update) return null;
  const postId: string = update.entityUrn ?? '';
  const socialDetail: string = update['*socialDetail'] ?? '';
  let ugcPostId = '';
  const ugcMatch = socialDetail.match(/ugcPost:(\d+)/);
  const activityMatch = socialDetail.match(/activity:(\d+)/);
  if (ugcMatch) ugcPostId = `urn:li:ugcPost:${ugcMatch[1]}`;
  else if (activityMatch) ugcPostId = `urn:li:activity:${activityMatch[1]}`;
  if (!postId) return null;
  return { postId, ugcPostId };
}

// ─── Types ────────────────────────────────────────────────────────────────────

type TMode = 'proxy' | 'extension';
type TTestAction = SequenceStepType | 'connection_request_note';

const ACTIONS: { value: TTestAction; label: string }[] = [
  { value: 'connection_request',      label: 'Connect (no note)' },
  { value: 'connection_request_note', label: 'Connect (with note)' },
  { value: 'message',                 label: 'Message'            },
  { value: 'post_like',               label: 'Post like'          },
  { value: 'post_comment',            label: 'Post comment'       },
];

interface ILogEntry {
  ts: string;
  label: string;
  payload: unknown;
  result: unknown;
  ok: boolean;
  ms: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function now() {
  return new Date().toLocaleTimeString('en-GB', { hour12: false, fractionalSecondDigits: 3 });
}

function isConnectionAction(action: TTestAction) {
  return action === 'connection_request' || action === 'connection_request_note';
}

function extensionOk(result: unknown): boolean {
  if (result instanceof Error || result === false) return false;

  const wrapped = result as { result?: unknown; error?: string };
  if (wrapped?.error) return false;

  const inner = wrapped?.result !== undefined ? wrapped.result : result;
  if (inner === false) return false;

  if (inner && typeof inner === 'object') {
    if ('success' in inner) {
      return (inner as { success?: boolean }).success === true;
    }
    if ('conversationId' in inner || 'messageId' in inner) {
      const { conversationId, messageId } = inner as { conversationId?: string | null; messageId?: string | null };
      return !!(conversationId || messageId);
    }
    // GraphQL / Voyager payloads (e.g. post like socialDashReactions)
    if ('data' in inner || 'included' in inner || 'elements' in inner) {
      return true;
    }
    return typeof inner === 'object' && inner !== null;
  }

  return inner === true;
}

// ─── Component ───────────────────────────────────────────────────────────────

const LinkedInTestPage: FunctionComponent = () => {
  const navigate = useNavigate();
  const { store: { user } } = useAuthStore();

  // Redirect non-superAdmins immediately
  useEffect(() => {
    if (user !== undefined && !user?.isSuperAdmin) {
      navigate(AppRoutes.HOME, { replace: true });
    }
  }, [user, navigate]);

  const { sendConnectionRequest, createOrSendMessage, getLoggedInLIUser, getProfilePosts, likeProfilePosts, commentProfilePosts } = useExtensionRequest();
  const { executeStep } = useCampaignsRequest();

  const [mode, setMode]     = useState<TMode>('extension');
  const [action, setAction] = useState<TTestAction>('connection_request');
  const [running, setRunning] = useState(false);
  const [log, setLog]         = useState<ILogEntry[]>([]);
  const logEndRef             = useRef<HTMLDivElement>(null);

  // ── Proxy fields ──────────────────────────────────────────────────────────
  const [campaignId,        setCampaignId]        = useState('');
  const [leadId,            setLeadId]            = useState('');
  const [stepIndex,         setStepIndex]         = useState('0');
  const [proxyContent,      setProxyContent]      = useState('');

  // ── Extension fields ─────────────────────────────────────────────────────
  const [profileInput,      setProfileInput]      = useState('');   // connection_request*
  const [connMessage,       setConnMessage]       = useState('');   // connection_request_note
  const [participantUrns,   setParticipantUrns]   = useState('');   // message  (comma-sep)
  const [messageText,       setMessageText]       = useState('');   // message
  const [profileId,         setProfileId]         = useState('');   // post_like / post_comment
  const [commentText,       setCommentText]       = useState('');   // post_comment

  // Sender URN — fetched live from extension's LinkedIn session (the only reliable source
  // for the internal fsd_profile alphanumeric ID used as mailboxUrn).
  const [senderUrn, setSenderUrn] = useState('');
  const [senderUrnLoading, setSenderUrnLoading] = useState(false);

  async function fetchSenderUrn() {
    setSenderUrnLoading(true);
    try {
      const liUser: any = await getLoggedInLIUser();
      const urn: string = liUser?.entityUrn ?? liUser?.miniProfile?.entityUrn ?? '';
      if (urn) {
        const full = urn.startsWith('urn:li:') ? urn : `urn:li:fsd_profile:${urn}`;
        setSenderUrn(full);
      }
    } finally {
      setSenderUrnLoading(false);
    }
  }

  useEffect(() => { fetchSenderUrn(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [log]);

  function addLog(label: string, payload: unknown, result: unknown, ok: boolean, ms: number) {
    setLog(prev => [...prev, { ts: now(), label, payload, result, ok, ms }]);
  }

  // ── Execute ───────────────────────────────────────────────────────────────

  async function handleExecute() {
    if (mode === 'extension' && isConnectionAction(action) && !profileInput.trim()) {
      addLog(`extension / ${action} [VALIDATION]`, {}, { error: 'LinkedIn URL or public identifier is required.' }, false, 0);
      return;
    }
    if (mode === 'extension' && action === 'connection_request_note' && !connMessage.trim()) {
      addLog('extension / connection_request_note [VALIDATION]', {}, { error: 'Note is required for Connect (with note).' }, false, 0);
      return;
    }
    if (mode === 'extension' && action === 'message') {
      if (!senderUrn.trim()) {
        addLog('extension / message [VALIDATION]', {}, { error: 'Sender LinkedIn URN is required.' }, false, 0);
        return;
      }
      if (!participantUrns.trim()) {
        addLog('extension / message [VALIDATION]', {}, { error: 'At least one recipient URN is required.' }, false, 0);
        return;
      }
      if (!messageText.trim()) {
        addLog('extension / message [VALIDATION]', {}, { error: 'Message text is required.' }, false, 0);
        return;
      }
    }
    if (mode === 'extension' && action === 'post_like' && !profileId.trim()) {
      addLog('extension / post_like [VALIDATION]', {}, { error: 'Target profile URN is required.' }, false, 0);
      return;
    }
    if (mode === 'extension' && action === 'post_comment') {
      if (!profileId.trim()) {
        addLog('extension / post_comment [VALIDATION]', {}, { error: 'Target profile URN is required.' }, false, 0);
        return;
      }
      if (!commentText.trim()) {
        addLog('extension / post_comment [VALIDATION]', {}, { error: 'Comment text is required.' }, false, 0);
        return;
      }
    }

    setRunning(true);
    const t0 = performance.now();
    let result: unknown;
    let ok = false;

    try {
      if (mode === 'proxy') {
        const idx = parseInt(stepIndex, 10);
        const payload = {
          stepType: action === 'connection_request_note' ? 'connection_request' : action,
          content: action === 'connection_request_note' ? proxyContent : (action === 'connection_request' ? '' : proxyContent),
          currentStepIndex: isNaN(idx) ? 0 : idx,
        };
        result = await executeStep(campaignId.trim(), leadId.trim(), payload);
        ok = !!(result as any)?.success;
        addLog(`proxy / ${action}`, { campaignId, leadId, stepIndex, content: proxyContent }, result, ok, Math.round(performance.now() - t0));

      } else {
        // extension mode
        switch (action) {
          case 'connection_request':
          case 'connection_request_note': {
            const note = action === 'connection_request_note' ? connMessage.trim() : '';
            result = await sendConnectionRequest(profileInput.trim(), note);
            ok = extensionOk(result);
            addLog(
              `extension / ${action}`,
              { publicIdentifier: profileInput.trim(), note },
              result instanceof Error ? { error: result.message } : result,
              ok,
              Math.round(performance.now() - t0)
            );
            break;
          }
          case 'message': {
            const rawUrns = participantUrns.split(',').map(s => s.trim()).filter(Boolean);
            result = await createOrSendMessage({
              profileId: senderUrn.trim(),
              participantUrns: rawUrns,
              text: messageText.trim(),
            }, { all: true });
            ok = extensionOk(result);
            addLog(
              'extension / message',
              { profileId: senderUrn.trim(), participantUrns: rawUrns, messageText: messageText.trim() },
              result instanceof Error ? { error: result.message } : result,
              ok,
              Math.round(performance.now() - t0)
            );
            break;
          }
          case 'post_like': {
            const profileUrn = toFsdProfileUrn(profileId);
            const rawPostsLike: any = await getProfilePosts(profileUrn);
            const postLike = extractLatestPostForTest(rawPostsLike);
            if (!postLike) {
              addLog('extension / post_like', { profileUrn }, { error: 'no_post_found — getProfilePosts returned no Update items' }, false, Math.round(performance.now() - t0));
              break;
            }
            const likePayload = {
              reactionAction: 'add',
              postId: postLike.postId,
              ugcPostId: postLike.ugcPostId,
              reactionType: 'LIKE',
              newReactionType: 'LIKE',
              graphqlQueryId: process.env.REACT_APP_SOCIAL_DASH_ADD_REACTION,
              feedSignalQueryId: process.env.REACT_APP_FEED_DASH_CLIENT_SIGNAL,
            };
            result = await likeProfilePosts(likePayload, { all: true });
            ok = extensionOk(result);
            addLog(
              'extension / post_like',
              { profileUrn, ...postLike, likePayload },
              result instanceof Error ? { error: result.message } : result,
              ok,
              Math.round(performance.now() - t0)
            );
            break;
          }
          case 'post_comment': {
            const profileUrn = toFsdProfileUrn(profileId);
            const rawPostsComment: any = await getProfilePosts(profileUrn);
            const postComment = extractLatestPostForTest(rawPostsComment);
            if (!postComment) {
              addLog('extension / post_comment', { profileUrn }, { error: 'no_post_found — getProfilePosts returned no Update items' }, false, Math.round(performance.now() - t0));
              break;
            }
            const commentPayload = {
              postId: postComment.postId,
              ugcPostId: postComment.ugcPostId,
              text: commentText.trim(),
              reactionType: 'submitComment',
              feedSignalQueryId: process.env.REACT_APP_FEED_DASH_CLIENT_SIGNAL,
            };
            result = await commentProfilePosts(commentPayload, { all: true });
            ok = extensionOk(result);
            addLog(
              'extension / post_comment',
              { profileUrn, ...postComment, commentPayload },
              result instanceof Error ? { error: result.message } : result,
              ok,
              Math.round(performance.now() - t0)
            );
            break;
          }
        }
      }
    } catch (err: any) {
      result = { error: err?.message ?? String(err) };
      addLog(`${mode} / ${action} [ERROR]`, {}, result, false, Math.round(performance.now() - t0));
    } finally {
      setRunning(false);
    }
  }

  if (!user?.isSuperAdmin) return null;

  const canExecuteExtension = (() => {
    if (mode !== 'extension') return true;
    if (isConnectionAction(action)) {
      return profileInput.trim().length > 0
        && (action !== 'connection_request_note' || connMessage.trim().length > 0);
    }
    if (action === 'message') {
      return senderUrn.trim().length > 0
        && participantUrns.trim().length > 0
        && messageText.trim().length > 0;
    }
    if (action === 'post_like') return profileId.trim().length > 0;
    if (action === 'post_comment') {
      return profileId.trim().length > 0 && commentText.trim().length > 0;
    }
    return true;
  })();

  // ─── Proxy fields by action ──────────────────────────────────────────────

  const renderProxyFields = () => (
    <FieldGrid>
      <Field label="Campaign ID *">
        <Input value={campaignId} onChange={e => setCampaignId(e.target.value)} placeholder="6650abc123…" />
      </Field>
      <Field label="Lead ID *">
        <Input value={leadId} onChange={e => setLeadId(e.target.value)} placeholder="6650def456…" />
      </Field>
      <Field label="Current step index *" style={{ flex: '0 0 160px' }}>
        <Input type="number" min={0} value={stepIndex} onChange={e => setStepIndex(e.target.value)} placeholder="0" />
      </Field>
      {(action === 'connection_request_note' || action === 'message' || action === 'post_comment') && (
        <Field
          label={
            action === 'connection_request_note' ? 'Note *'
            : action === 'message' ? 'Message text *'
            : 'Comment text *'
          }
          style={{ flex: '1 0 100%' }}
        >
          <Textarea
            rows={3}
            value={proxyContent}
            onChange={e => setProxyContent(e.target.value)}
            placeholder={
              action === 'connection_request_note' ? "Hi, I'd like to connect…"
              : action === 'message' ? 'Hi {{firstName}}, …'
              : 'Great post!'
            }
          />
        </Field>
      )}
    </FieldGrid>
  );

  // ─── Extension fields by action ──────────────────────────────────────────

  const renderExtensionFields = () => {
    switch (action) {
      case 'connection_request':
      case 'connection_request_note':
        return (
          <FieldGrid>
            <Field label="LinkedIn URL or public identifier *" style={{ flex: '1 0 100%' }}>
              <Input
                value={profileInput}
                onChange={e => setProfileInput(e.target.value)}
                placeholder="https://www.linkedin.com/in/john-doe  or  john-doe"
              />
            </Field>
            {action === 'connection_request_note' && (
              <Field label="Note *" style={{ flex: '1 0 100%' }}>
                <Textarea
                  rows={3}
                  maxLength={200}
                  value={connMessage}
                  onChange={e => setConnMessage(e.target.value)}
                  placeholder="Hi, I'd like to connect. Thanks"
                />
              </Field>
            )}
          </FieldGrid>
        );
      case 'message':
        return (
          <FieldGrid>
            <Field label="Your LinkedIn URN (sender / mailboxUrn) *" style={{ flex: '1 0 100%' }}>
              <Row gap="6px" alignItems>
                <Input
                  value={senderUrnLoading ? 'Fetching from extension…' : senderUrn}
                  onChange={e => setSenderUrn(e.target.value)}
                  placeholder="urn:li:fsd_profile:ACoAAEvM…"
                  disabled={senderUrnLoading}
                  style={{ flex: 1 }}
                />
                <RefreshBtn type="button" onClick={fetchSenderUrn} disabled={senderUrnLoading} title="Re-fetch from extension">
                  ↻
                </RefreshBtn>
              </Row>
            </Field>
            <Field label="Recipient URN(s) * (internal LinkedIn ID, comma-sep)" style={{ flex: '1 0 100%' }}>
              <Input
                value={participantUrns}
                onChange={e => setParticipantUrns(e.target.value)}
                placeholder="ACoAABNRr5…  or  urn:li:fsd_profile:ACoAA…  (prefix added automatically)"
              />
            </Field>
            <Field label="Message text *" style={{ flex: '1 0 100%' }}>
              <Textarea rows={3} value={messageText} onChange={e => setMessageText(e.target.value)} placeholder="Hello …" />
            </Field>
          </FieldGrid>
        );
      case 'post_like':
        return (
          <FieldGrid>
            <Field label="Target profile URN * (urn:li:fsd_profile:XXX)" style={{ flex: '1 0 100%' }}>
              <Input
                value={profileId}
                onChange={e => setProfileId(e.target.value)}
                placeholder="urn:li:fsd_profile:ACoAABNRr5…"
              />
            </Field>
          </FieldGrid>
        );
      case 'post_comment':
        return (
          <FieldGrid>
            <Field label="Target profile URN * (urn:li:fsd_profile:XXX)" style={{ flex: '1 0 100%' }}>
              <Input
                value={profileId}
                onChange={e => setProfileId(e.target.value)}
                placeholder="urn:li:fsd_profile:ACoAABNRr5…"
              />
            </Field>
            <Field label="Comment text *" style={{ flex: '1 0 100%' }}>
              <Textarea rows={3} value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Great post!" />
            </Field>
          </FieldGrid>
        );
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <PageWrap>
      <Header>
        <Column gap="2px">
          <Text $heading3 $bold>LinkedIn Action Tester</Text>
          <Badge>superAdmin only · /dev/linkedin-test</Badge>
        </Column>
      </Header>

      <Card>
        {/* Mode toggle */}
        <Section>
          <SectionLabel>Execution mode</SectionLabel>
          <ModeRow>
            {(['proxy', 'extension'] as TMode[]).map(m => (
              <ModeBtn key={m} $active={mode === m} onClick={() => setMode(m)}>
                {m === 'proxy' ? 'LeadDelta Cloud' : 'Chrome Extension'}
              </ModeBtn>
            ))}
          </ModeRow>
          {mode === 'proxy' && (
            <ModeHint>
              Calls <code>POST /campaigns/:id/leads/:id/execute-step</code> through the backend cloud proxy service.
              Requires an active campaign with a connected LinkedIn account.
            </ModeHint>
          )}
          {mode === 'extension' && (
            <ModeHint>
              Sends a message directly to the Chrome extension via <code>chrome.runtime.sendMessage</code>.
              Extension must be installed and LinkedIn must be open in a tab.
            </ModeHint>
          )}
        </Section>

        {/* Action tabs */}
        <Section>
          <SectionLabel>Action</SectionLabel>
          <ActionTabs>
            {ACTIONS.map(a => (
              <ActionTab key={a.value} $active={action === a.value} onClick={() => setAction(a.value)}>
                {a.label}
              </ActionTab>
            ))}
          </ActionTabs>
        </Section>

        {/* Fields */}
        <Section>
          <SectionLabel>Parameters</SectionLabel>
          {mode === 'proxy' ? renderProxyFields() : renderExtensionFields()}
        </Section>

        {/* Execute */}
        <Row gap="12px" alignItems>
          <ExecBtn
            onClick={handleExecute}
            disabled={running || !canExecuteExtension}
          >
            {running ? 'Running…' : `Execute  →  ${ACTIONS.find(a => a.value === action)?.label}`}
          </ExecBtn>
          {log.length > 0 && (
            <ClearBtn onClick={() => setLog([])}>Clear log</ClearBtn>
          )}
        </Row>
      </Card>

      {/* Log */}
      {log.length > 0 && (
        <LogPanel>
          <LogHeader>
            <SectionLabel style={{ marginBottom: 0 }}>Result log</SectionLabel>
          </LogHeader>
          {log.map((entry, i) => (
            <LogEntry key={i} $ok={entry.ok}>
              <LogMeta>
                <LogTs>{entry.ts}</LogTs>
                <LogLabel $ok={entry.ok}>{entry.label}</LogLabel>
                <LogMs>{entry.ms} ms</LogMs>
                <LogStatus $ok={entry.ok}>{entry.ok ? 'OK' : 'FAIL'}</LogStatus>
              </LogMeta>
              <LogBlock>
                <LogBlockTitle>Payload</LogBlockTitle>
                <Pre>{JSON.stringify(entry.payload, null, 2)}</Pre>
                <LogBlockTitle>Response</LogBlockTitle>
                <Pre>{JSON.stringify(entry.result, null, 2)}</Pre>
              </LogBlock>
            </LogEntry>
          ))}
          <div ref={logEndRef} />
        </LogPanel>
      )}
    </PageWrap>
  );
};

// ─── Sub-component ────────────────────────────────────────────────────────────

const Field: FunctionComponent<{ label: string; style?: React.CSSProperties; children: React.ReactNode }> = ({ label, style, children }) => (
  <FieldWrap style={style}>
    <FieldLabel>{label}</FieldLabel>
    {children}
  </FieldWrap>
);

// ─── Styles ──────────────────────────────────────────────────────────────────

const PageWrap = styled.div`
  width: 100%; max-width: 960px; margin: 0 auto;
  padding: 24px 24px 40px;
  display: flex; flex-direction: column; gap: 16px;
  min-height: 100%; overflow-y: auto;
`;

const Header = styled.div`display: flex; align-items: flex-start; justify-content: space-between;`;

const Badge = styled.span`
  font-family: NunitoSans; font-size: 11px; font-weight: 700;
  background: #fef3c7; color: #92400e;
  border: 1px solid #fcd34d; border-radius: 4px; padding: 2px 7px;
  letter-spacing: 0.04em; display: inline-block;
`;

const Card = styled.div`
  background: ${styles.colors.white}; border: 1px solid #eef2f7;
  border-radius: 14px; padding: 22px 24px;
  display: flex; flex-direction: column; gap: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.04);
`;

const Section = styled.div`display: flex; flex-direction: column; gap: 8px;`;

const SectionLabel = styled.div`
  font-family: NunitoSans; font-size: 11px; font-weight: 800;
  text-transform: uppercase; letter-spacing: 0.07em;
  color: ${styles.colors.black400}; margin-bottom: 2px;
`;

const ModeRow = styled.div`display: flex; gap: 8px;`;

const ModeBtn = styled.button<{ $active: boolean }>`
  height: 34px; padding: 0 16px; border-radius: 8px; cursor: pointer;
  font-family: NunitoSans; font-size: 13px; font-weight: 700;
  border: 2px solid ${({ $active }) => $active ? styles.colors.primary600 : '#dde3ee'};
  background: ${({ $active }) => $active ? '#f0f4ff' : styles.colors.white};
  color: ${({ $active }) => $active ? styles.colors.primary600 : styles.colors.black400};
  transition: all 0.12s;
  &:hover { border-color: ${styles.colors.primary600}; }
`;

const ModeHint = styled.p`
  font-family: NunitoSans; font-size: 11px; color: ${styles.colors.black300};
  line-height: 1.6; margin: 0;
  code { background: #eef2ff; color: ${styles.colors.primary600}; padding: 1px 5px; border-radius: 4px; font-size: 11px; }
`;

const ActionTabs = styled.div`display: flex; gap: 4px; flex-wrap: wrap;`;

const ActionTab = styled.button<{ $active: boolean }>`
  height: 30px; padding: 0 14px; border-radius: 6px; cursor: pointer;
  font-family: NunitoSans; font-size: 12px; font-weight: 700;
  border: 1.5px solid ${({ $active }) => $active ? styles.colors.primary600 : '#dde3ee'};
  background: ${({ $active }) => $active ? styles.colors.primary600 : styles.colors.white};
  color: ${({ $active }) => $active ? styles.colors.white : styles.colors.black400};
  transition: all 0.12s;
  &:hover { border-color: ${styles.colors.primary600}; }
`;

const FieldGrid = styled.div`display: flex; gap: 10px; flex-wrap: wrap; align-items: flex-start;`;

const FieldWrap = styled.label`display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 160px;`;

const FieldLabel = styled.span`
  font-family: NunitoSans; font-size: 11px; font-weight: 700; color: ${styles.colors.black400};
`;

const inputBase = `
  font-family: NunitoSans; font-size: 13px; color: ${styles.colors.black600};
  background: ${styles.colors.white}; outline: none;
  border: 1.5px solid #dde3ee; border-radius: 8px; padding: 0 12px; width: 100%; box-sizing: border-box;
  &:focus { border-color: ${styles.colors.primary600}; box-shadow: 0 0 0 3px rgba(89,100,255,0.08); }
  &::placeholder { color: ${styles.colors.black300}; }
`;

const Input = styled.input`
  ${inputBase}
  height: 34px;
`;

const Textarea = styled.textarea`
  ${inputBase}
  padding: 8px 12px; resize: vertical; min-height: 70px; line-height: 1.5;
`;

const ExecBtn = styled.button`
  height: 38px; padding: 0 22px;
  background: ${styles.colors.primary600}; color: ${styles.colors.white};
  border: none; border-radius: 8px;
  font-family: NunitoSans; font-size: 13px; font-weight: 800; cursor: pointer;
  &:hover:not(:disabled) { background: ${styles.colors.primary400}; }
  &:disabled { background: ${styles.colors.black200}; cursor: default; }
`;

const ClearBtn = styled.button`
  height: 38px; padding: 0 16px;
  background: transparent; color: ${styles.colors.black400};
  border: 1.5px solid #dde3ee; border-radius: 8px;
  font-family: NunitoSans; font-size: 12px; font-weight: 600; cursor: pointer;
  &:hover { background: ${styles.colors.black100}; }
`;

const LogPanel = styled.div`
  border: 1px solid #eef2f7; border-radius: 14px; overflow: hidden;
  background: #f8f9fc;
  flex: 1; min-height: 400px; overflow-y: auto;
  display: flex; flex-direction: column;
`;

const LogHeader = styled.div`
  padding: 12px 16px; background: ${styles.colors.white};
  border-bottom: 1px solid #eef2f7;
  position: sticky; top: 0; z-index: 1;
`;

const LogEntry = styled.div<{ $ok: boolean }>`
  border-left: 3px solid ${({ $ok }) => $ok ? '#22c55e' : '#ef4444'};
  border-bottom: 1px solid #eef2f7;
  background: ${styles.colors.white};
  &:last-child { border-bottom: none; }
`;

const LogMeta = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 8px 16px; cursor: pointer;
`;

const LogTs = styled.span`font-family: monospace; font-size: 11px; color: ${styles.colors.black300};`;

const LogLabel = styled.span<{ $ok: boolean }>`
  font-family: NunitoSans; font-size: 12px; font-weight: 700;
  color: ${({ $ok }) => $ok ? '#166534' : '#991b1b'};
  flex: 1;
`;

const LogMs = styled.span`font-family: monospace; font-size: 11px; color: ${styles.colors.black300};`;

const LogStatus = styled.span<{ $ok: boolean }>`
  font-family: NunitoSans; font-size: 11px; font-weight: 800;
  padding: 2px 7px; border-radius: 4px;
  background: ${({ $ok }) => $ok ? '#dcfce7' : '#fee2e2'};
  color: ${({ $ok }) => $ok ? '#166534' : '#991b1b'};
`;

const LogBlock = styled.div`padding: 0 16px 12px;`;

const LogBlockTitle = styled.div`
  font-family: NunitoSans; font-size: 10px; font-weight: 800;
  text-transform: uppercase; letter-spacing: 0.06em;
  color: ${styles.colors.black300}; margin: 8px 0 4px;
`;

const RefreshBtn = styled.button`
  height: 34px; width: 34px; flex-shrink: 0;
  border: 1.5px solid #dde3ee; border-radius: 8px;
  background: ${styles.colors.white}; cursor: pointer;
  font-size: 16px; display: flex; align-items: center; justify-content: center;
  &:hover:not(:disabled) { border-color: ${styles.colors.primary600}; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const Pre = styled.pre`
  font-family: 'Courier New', monospace; font-size: 12px; line-height: 1.6;
  background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 6px;
  padding: 10px 14px; margin: 0;
  overflow-x: auto; overflow-y: auto;
  max-height: 500px;
  white-space: pre-wrap; word-break: break-all;
  color: ${styles.colors.black600};
`;

export default LinkedInTestPage;
